// Where a print job went, in words (spec/13 §11; F-OPS-08, F-FNB-05). Every print — a kitchen ticket, a receipt,
// a test page — is a print job the server routed: to the `browser` (the console prints the job's document
// itself), a desk `device` or a `cloud` printer (it is on its way; the console says where), or, for a kitchen
// ticket no printer could take, the kitchen `display` (the ticket stays on the board and staff were alerted).

import type { PrintJob, PrintTarget } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import type { ReactNode } from 'react';

import { Notice } from '../../ui';
import { usePrintDocument } from './print-document';

export type PrintOutcome =
  | { readonly kind: 'printed' }
  | { readonly kind: 'blocked' }
  | { readonly kind: 'sent'; readonly printer: string }
  | { readonly kind: 'display' }
  /** `message`: why, in the words for the server's problem code, when the refusal named one. */
  | { readonly kind: 'failed'; readonly message?: string };

/**
 * Act on a job the server routed: print a `browser` job's document now, and say what happened to the rest.
 * `printer` names where a device or cloud job went — the printer's name, or "the kitchen printer".
 */
export function usePrintJobHandler(): (job: PrintJob, target: PrintTarget, title: string, printer: string) => PrintOutcome {
  const print = usePrintDocument();
  return (job, target, title, printer) => {
    if (target === 'browser') return print(job.document, title) ? { kind: 'printed' } : { kind: 'blocked' };
    if (target === 'display') return { kind: 'display' };
    return { kind: 'sent', printer };
  };
}

/** The outcome as an inline note; nothing for a page the browser is printing now. */
export function PrintOutcomeNote({ outcome, className }: { readonly outcome: PrintOutcome | null; readonly className?: string }): ReactNode {
  const { t } = useTranslation('console');
  if (outcome === null || outcome.kind === 'printed') return null;
  const extra = className === undefined ? {} : { className };
  switch (outcome.kind) {
    case 'sent':
      return (
        <Notice tone="ok" role="status" {...extra}>
          {t('printing.sentTo', { printer: outcome.printer })}
        </Notice>
      );
    case 'display':
      return (
        <Notice tone="warn" role="status" {...extra}>
          {t('printing.onDisplay')}
        </Notice>
      );
    case 'blocked':
      return (
        <Notice tone="warn" role="status" {...extra}>
          {t('printing.popupBlocked')}
        </Notice>
      );
    case 'failed':
      return (
        <Notice tone="danger" role="alert" {...extra}>
          {outcome.message ?? t('printing.failed')}
        </Notice>
      );
  }
}
